import axios from 'axios';

// ---- Media change notifications (in-memory event bus) ----
export type MediaChangeAction = 'added' | 'deleted' | 'updated';
export type MediaChangeEvent = {
  organizationId: string;
  action: MediaChangeAction;
  item?: any; // MediaItem shape; kept as any to avoid circular imports
  path?: string; // used for deletes when only path is known
};

type MediaChangeListener = (evt: MediaChangeEvent) => void;
const listeners = new Set<MediaChangeListener>();

function notifyMediaChange(evt: MediaChangeEvent) {
  for (const cb of listeners) {
    try {
      cb(evt);
    } catch (e) {
      console.error('MediaChangeListener error', e);
    }
  }
}

function subscribeMediaChanges(cb: MediaChangeListener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// NOTE: This helper centralizes "API base URL" resolution so we never hardcode localhost.
function getApiBaseUrl(): string {
  // Prefer Vite convention. Support both names:
  // - VITE_API_URL (primary)
  // - VITE_API_BASE_URL (older code)
  const env = (import.meta as any)?.env;
  const viteUrl = env?.VITE_API_URL;
  const viteBase = env?.VITE_API_BASE_URL;
  const chosen = (typeof viteUrl === 'string' && viteUrl.trim())
    ? viteUrl
    : (typeof viteBase === 'string' && viteBase.trim() ? viteBase : '');

  if (chosen) return chosen.replace(/\/+$/, '');

  // Fallback to axios default baseURL if configured elsewhere
  try {
    const ax: any = (axios as any);
    const base = ax?.defaults?.baseURL;
    if (typeof base === 'string' && base.trim()) {
      return base.trim().replace(/\/+$/, '');
    }
  } catch { /* ignore */ }

  // Default: same-origin behind a reverse proxy
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

function joinUrl(base: string, path: string): string {
  const b = (base ?? '').replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

function getApiOrigin(): string {
  // Media is served by StaticFiles at /media on the API host.
  // If VITE_API_URL includes /api, we must use only the origin (scheme+host+port).
  const base = getApiBaseUrl();
  if (!base) return '';

  // absolute URL => use its origin
  if (/^https?:\/\//i.test(base)) {
    try {
      return new URL(base).origin;
    } catch {
      // fall through
    }
  }

  // relative (e.g. "/api") => cannot infer :5001; will be same-origin
  if (typeof window !== 'undefined' && window.location?.origin) {
    // Dev heuristic: if running on localhost dev server without explicit API URL,
    // prefer typical API port 5001 so /media uses the API host instead of 8080/5173.
    const { protocol, hostname, port } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocal) {
      const devPorts = new Set(['3000', '5173', '5174', '8080']);
      if (!base || base === '/api' || devPorts.has(port)) {
        const env = (import.meta as any)?.env;
        const apiPort = (env?.VITE_API_PORT && String(env.VITE_API_PORT).trim()) || '5001';
        return `${protocol}//${hostname}:${apiPort}`;
      }
    }
    return window.location.origin;
  }

  return base;
}

function getApiControllerRoot(): string {
  // controllers live under /api. If VITE_API_URL already ends in /api, don't double it.
  const base = getApiBaseUrl();
  if (!base) return '/api';
  return base.endsWith('/api') ? base : joinUrl(base, '/api');
}

function encodePathSegments(path: string): string {
  return (path || '')
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
}

function noCacheParams(): Record<string, string> {
  return { _ts: String(Date.now()) };
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Convert an API relative URL (e.g. "/media/xyz.png") into an absolute URL.
 * - If already absolute, returns as-is
 * - If VITE_API_URL (or VITE_API_BASE_URL) is defined, prefixes with that
 * - Otherwise falls back to window.location.origin (same-origin deployments)
 */
function toAbsoluteUrl(urlOrPath: string): string {
  if (!urlOrPath) return urlOrPath;
  if (isAbsoluteUrl(urlOrPath)) return urlOrPath;
  if (!urlOrPath.startsWith('/')) return urlOrPath;

  // IMPORTANT: for absolute paths like "/media/..." use API ORIGIN, not React origin.
  return joinUrl(getApiOrigin(), urlOrPath);
}

const API_BASE_URL = joinUrl(getApiControllerRoot(), '/MediaStorage');

interface UploadResponse {
  success: boolean;
  path?: string;
  message?: string;
}

class MediaService {
  toAbsoluteUrl(urlOrPath: string): string {
    return toAbsoluteUrl(urlOrPath);
  }

  subscribeMediaChanges(cb: MediaChangeListener): () => void {
    return subscribeMediaChanges(cb);
  }

  /**
   * Get a file from media storage
   */
  async getFile(organizationId: string, filePath: string): Promise<Blob> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/${organizationId}/files/${filePath}`,
        {
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching file:', error);
      throw error;
    }
  }

  /**
   * Upload a file to media storage
   * @param mediaCategoryId Optional media category ID to associate with the file
   */
  async uploadFile(
    organizationId: string,
    filePath: string,
    file: File,
    mediaCategoryId?: string
  ): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      formData.append('File', file);
      
      if (mediaCategoryId) {
        formData.append('MediaCategoryId', mediaCategoryId);
      }

      const response = await axios.post(
        `${API_BASE_URL}/${organizationId}/files/${filePath}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const data = response?.data;
      const item =
        data?.item ??
        data?.file ??
        data ?? {
          name: file.name,
          path: data?.path ?? data?.filePath ?? data?.key ?? filePath,
          url: data?.url ?? data?.fileUrl ?? data?.downloadUrl,
          mediaCategoryId: mediaCategoryId ?? null,
          size: file.size,
          uploadedAt: new Date().toISOString(),
        };

      notifyMediaChange({
        organizationId,
        action: 'added',
        item,
      });

      return {
        success: true,
        path: filePath,
        message: 'File uploaded successfully',
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Update/Replace a file in media storage
   * @param mediaCategoryId Optional media category ID to associate with the file
   */
  async updateFile(
    organizationId: string,
    filePath: string,
    file: File,
    mediaCategoryId?: string
  ): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      formData.append('File', file);
      
      if (mediaCategoryId) {
        formData.append('MediaCategoryId', mediaCategoryId);
      }

      const response = await axios.put(
        `${API_BASE_URL}/${organizationId}/files/${filePath}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const data = response?.data;
      const item =
        data?.item ??
        data?.file ??
        data ?? {
          name: file.name,
          path: data?.path ?? data?.filePath ?? data?.key ?? filePath,
          url: data?.url ?? data?.fileUrl ?? data?.downloadUrl,
          mediaCategoryId: mediaCategoryId ?? null,
          size: file.size,
          uploadedAt: new Date().toISOString(),
        };

      notifyMediaChange({
        organizationId,
        action: 'updated',
        item,
      });

      return {
        success: true,
        path: filePath,
        message: 'File updated successfully',
      };
    } catch (error) {
      console.error('Error updating file:', error);
      throw error;
    }
  }

  /**
   * Delete a file from media storage
   */
  async deleteFile(organizationId: string, filePath: string): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/${organizationId}/files/${filePath}`
      );
      notifyMediaChange({ organizationId, action: 'deleted', path: filePath });
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Get all files in an organization
   */
  async getOrganizationFiles(
    organizationId: string,
    opts?: { bustCache?: boolean }
  ): Promise<any[]> {
    try {
      const bustCache = opts?.bustCache ?? true;
      const response = await axios.get(
        `${API_BASE_URL}/${organizationId}/files`,
        {
          params: bustCache ? noCacheParams() : undefined,
          headers: bustCache ? { 'Cache-Control': 'no-store', Pragma: 'no-cache' } : undefined,
        }
      );
      // Transform API response to match MediaItem interface
      const files = response.data || [];
      return files.map((file: any) => ({
        path: file.path,
        name: file.fileName || file.path,
        size: file.sizeBytes,
        uploadedAt: file.lastModifiedUtc,
        url: file.url,
        mediaCategoryId: file.mediaCategoryId,
      }));
    } catch (error) {
      console.error('Error fetching organization files:', error);
      return [];
    }
  }

  /**
   * Get a direct download URL for a file
   */
  getFileUrl(organizationId: string, filePath: string): string {
    return `${API_BASE_URL}/${organizationId}/files/${filePath}`;
  }

  getDirectUrl(organizationId: string, path: string): string {
    // /startswith badges workaround
    if (path.startsWith('/badges')) {
      return path;
    }
    //
    const org = encodeURIComponent(organizationId);
    const rel = encodePathSegments((path || '').replace(/^\/+/, ''));
    return joinUrl(getApiOrigin(), `/media/${org}/${rel}`);
  }

  /**
   * Format file size in human-readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, maxSizeMB: number = 50): { valid: boolean; error?: string } {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `File size exceeds ${maxSizeMB}MB limit. Current size: ${this.formatFileSize(file.size)}`,
      };
    }

    return { valid: true };
  }
}

const mediaService = new MediaService();

// Attach helper to service so callers (pages/components) can resolve absolute URLs
(mediaService as any).toAbsoluteUrl = toAbsoluteUrl;

export default mediaService;
